import { useEffect, useState, type FormEvent } from "react";
import { api, apiPdf, puede } from "../api.ts";
import { imprimir as imprimirHoja } from "../imprimir.ts";
import { guardarPdf, type ResultadoGuardado } from "../guardarArchivo.ts";
import { AvisoError, Seccion, useAccion } from "../pantallas/comun.tsx";
import { EnviarWhatsApp } from "./EnviarWhatsApp.tsx";

/** Documentos que se pueden mandar por WhatsApp desde la vista previa: se reconocen por la ruta del PDF (/api/documentos/<tipo>/<id>/pdf o /archivo). */
const RUTA_ENVIABLE = /^\/api\/documentos\/(receta|certificado|consentimiento|indicacion|abono|presupuesto)\/([0-9a-f-]{36})\/(?:pdf|archivo)$/i;

interface Estado { configurada: boolean; propia?: boolean; vencida?: boolean; diasRestantes?: number; info?: { titular: string; emisor: string; desde: string; hasta: string; serie: string } }
const fecha = (iso: string) => new Date(iso).toLocaleDateString("es-EC", { day: "numeric", month: "long", year: "numeric" });

/** La firma electrónica (.p12) del profesional: con ella, certificados, recetas y consentimientos salen firmados digitalmente. */
export function MiFirma() {
  const [e, setE] = useState<Estado | null>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [clave, setClave] = useState("");
  const [abierto, setAbierto] = useState(false);
  const [ok, setOk] = useState("");
  const a = useAccion();
  const cargar = () => api<Estado>("GET", "/api/mi-firma").then(setE).catch(() => setE(null));
  useEffect(() => { if (puede("clinico:firmar")) cargar(); }, []);
  if (!puede("clinico:firmar") || !e) return null;

  async function subir(ev: FormEvent) {
    ev.preventDefault();
    if (!archivo) return;
    const b64 = await new Promise<string>((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = rej; r.readAsDataURL(archivo); });
    const r = await a.correr(() => api<Estado>("POST", "/api/mi-firma", { archivo: b64, clave }));
    if (r) { setE(r); setArchivo(null); setClave(""); setAbierto(false); setOk("Firma cargada y verificada. Desde ahora tus certificados, recetas y consentimientos salen firmados digitalmente."); }
  }
  async function quitar() {
    if (!confirm("¿Quitar tu firma electrónica de Donna? Los documentos ya firmados no cambian; los nuevos saldrán sin firma digital.")) return;
    const r = await a.correr(() => api<Estado>("DELETE", "/api/mi-firma"));
    if (r) { setE(r); setOk(""); }
  }
  const info = e.info;
  const estado = !e.configurada ? <span className="chip rojo">Sin firma digital</span> : e.vencida ? <span className="chip rojo">Vencida</span> : <span className="chip azul">Activa</span>;
  return (
    <Seccion titulo="Mi firma electrónica" acciones={estado}>
      {e.configurada && info ? (
        <p style={{ margin: 0, fontSize: 14 }}>
          <b>{info.titular}</b> · emitida por {info.emisor || "—"}<br />
          Válida hasta el {fecha(info.hasta)}{e.vencida ? " · VENCIDA: renuévala con tu entidad certificadora" : e.diasRestantes !== undefined && e.diasRestantes < 30 ? ` · vence en ${e.diasRestantes} días: renuévala pronto` : ""}
          {!e.propia && <><br /><span className="muted">Se usa la firma que cargaste en otra de tus clínicas.</span></>}
        </p>
      ) : (
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>Sube tu firma electrónica (archivo .p12 o .pfx) una sola vez. Donna firmará digitalmente en PDF cada certificado, receta y consentimiento que emitas, con tu nombre y la fecha. Si no la cargas, los documentos se imprimen como siempre, sin firma digital.</p>
      )}
      {ok && <div className="aviso ok" role="status">{ok}</div>}
      <div className="fila">
        <button className="btn chico" onClick={() => setAbierto(!abierto)} aria-expanded={abierto}>{e.configurada ? "Reemplazar firma" : "Cargar mi firma"}</button>
        {e.configurada && e.propia && <button className="btn chico peligro" onClick={quitar} disabled={a.ocupado}>Quitar</button>}
      </div>
      {abierto && (
        <form className="fila" onSubmit={subir} aria-label="Cargar firma electrónica" style={{ alignItems: "end", flexWrap: "wrap" }}>
          <label className="campo" style={{ flex: "2 1 240px" }}>Archivo de la firma (.p12 / .pfx)
            <input type="file" accept=".p12,.pfx,application/x-pkcs12" onChange={(x) => setArchivo(x.target.files?.[0] ?? null)} /></label>
          <label className="campo" style={{ flex: "1 1 180px" }}>Contraseña de la firma
            <input className="entrada" type="password" autoComplete="off" value={clave} onChange={(x) => setClave(x.target.value)} /></label>
          <button className="btn pri" disabled={a.ocupado || !archivo || !clave}>Subir firma</button>
          <small className="muted" style={{ flexBasis: "100%" }}>El archivo y la contraseña se guardan cifrados en el servidor y solo se usan para firmar tus documentos.</small>
        </form>
      )}
      <AvisoError error={a.error} />
    </Seccion>
  );
}

/**
 * Muestra un PDF firmado del servidor dentro de Donna: ver, imprimir o descargar.
 * Las páginas se dibujan con pdf.js (no con el visor del navegador): en Safari de Mac y en iPad el visor propio del navegador no mostraba
 * el PDF dentro de la página (salía en blanco). Imprimir usa el mismo mecanismo de las demás hojas (imprimir.ts), que funciona en todos los equipos.
 */
export function VisorPdf({ ruta, titulo, alCerrar, alternativa }: { ruta: string; titulo: string; alCerrar: () => void; alternativa?: () => void }) {
  const [url, setUrl] = useState("");
  const [paginas, setPaginas] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [a5, setA5] = useState(false);
  const [bajado, setBajado] = useState<ResultadoGuardado | null>(null);
  const nombreArchivo = `${titulo.replace(/[^\p{L}\p{N}._-]+/gu, "_")}.pdf`;
  useEffect(() => {
    let vivo = true, u = "";
    (async () => {
      try {
        const { url: x, bytes } = await apiPdf(ruta);
        u = x;
        if (!vivo) return;
        setUrl(x);
        const pdfjs = await import("pdfjs-dist/legacy/build/pdf.min.mjs");
        pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.js";
        const doc = await pdfjs.getDocument({ data: new Uint8Array(bytes) }).promise;
        const imgs: string[] = [];
        for (let n = 1; n <= doc.numPages && vivo; n++) {
          const pag = await doc.getPage(n);
          const vista = pag.getViewport({ scale: 1 });
          const escala = Math.min(3, 1700 / vista.width); // ~1700 px de ancho: nítido en pantalla y al imprimir
          if (n === 1) setA5(vista.width < 480); // A4 mide ~595 pt de ancho y A5 ~420
          const v = pag.getViewport({ scale: escala });
          const lienzo = document.createElement("canvas");
          lienzo.width = Math.floor(v.width); lienzo.height = Math.floor(v.height);
          const ctx = lienzo.getContext("2d")!;
          ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, lienzo.width, lienzo.height);
          await pag.render({ canvasContext: ctx, viewport: v }).promise;
          imgs.push(lienzo.toDataURL("image/jpeg", 0.92));
          if (vivo) setPaginas([...imgs]);
        }
      } catch (e) { if (vivo) setError(e instanceof Error ? e.message : "No se pudo abrir el documento."); }
    })();
    return () => { vivo = false; if (u) URL.revokeObjectURL(u); };
  }, [ruta]);
  useEffect(() => { const f = (ev: KeyboardEvent) => { if (ev.key === "Escape") alCerrar(); }; addEventListener("keydown", f); return () => removeEventListener("keydown", f); }, []);
  const enviable = RUTA_ENVIABLE.exec(ruta);
  return (
    <div className="visor visor-pdf" role="dialog" aria-modal="true" aria-label={titulo}>
      {error ? <div className="aviso error" style={{ maxWidth: 480 }}>{error}{alternativa && <> <button className="btn chico" onClick={alternativa}>Imprimir con la hoja de siempre</button></>}</div>
        : paginas.length ? (
          <div className="visor-paginas">
            {paginas.map((p, i) => <img key={i} src={p} alt={`${titulo}, página ${i + 1}`} />)}
          </div>
        ) : <p style={{ color: "#fff" }}>Abriendo el documento…</p>}
      {/* lo que se imprime: las mismas páginas, una por hoja (sin reducirlas a una sola) */}
      {paginas.length > 0 && (
        <div className="solo-impresion" data-sin-ajuste="1" data-pagina={a5 ? "A5" : "A4"}>
          {paginas.map((p, i) => <img key={i} className="pag-pdf" src={p} alt="" />)}
        </div>
      )}
      <div className="fila visor-barra">
        <span>{titulo}{ruta.endsWith("/pdf") ? " · firmado digitalmente" : ""}</span>
        {paginas.length > 0 && <button className="btn chico pri" onClick={() => imprimirHoja()}>Imprimir</button>}
        {url && <button className="btn chico" onClick={async () => {
          setBajado(null);
          try { setBajado(await guardarPdf(nombreArchivo, () => fetch(url).then((r) => r.blob()))); } catch { setError("No se pudo guardar el PDF."); }
        }}>Descargar PDF</button>}
        {bajado === "descarga" && <span className="muted" role="status" style={{ fontSize: 12, maxWidth: 320 }}>Se guardó como «{nombreArchivo}» en la carpeta Descargas. En Safari, toca la flecha ↓ de la barra superior para abrirlo.</span>}
        {bajado === "guardado" && <span className="muted" role="status" style={{ fontSize: 12 }}>PDF guardado.</span>}
        {enviable && <EnviarWhatsApp tipo={enviable[1].toLowerCase() as "receta" | "certificado" | "consentimiento" | "indicacion" | "abono" | "presupuesto"} id={enviable[2].toLowerCase()} />}
        <button className="btn chico" onClick={alCerrar}>Cerrar</button>
      </div>
    </div>
  );
}
